import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import {
  IAbsenceTypes,
  ProfileData,
  RecurringDate,
  UserUpdateData
} from '../../../models/profile/profile.model';
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../../../services/profile/profile-data.service';
import { UploadHelper } from '../../../helper/uploadHelper';
import { IFileDocument } from '../../../models/shared.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PhoneNumberHelper } from '../../../helper/phone-number.helper';
import { UserProfileShareService } from '../../../services/shared-service/user-profile-share.service';
import { ResourceLanguage, ResourceProvinces } from '../../../models/resources/resources.model';
import { RESOURCES_DATA_SERVICE, ResourcesDataService } from '../../../services/resources/resources-data.service';
import { AddressHelper } from '../../../helper/address.helper';
import { environment } from '../../../environments/environment';
import { HistoryCommentComponent } from '../contact-details/comment/comment.component';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../services/action/data.service';
import { HideLoader, ShowLoader } from '../../store/loader/loader.actions';
import { Store } from '@ngxs/store';
import { IActionTypes, NotInterestedTypes } from "../../../models/action.model";
import { ActionHelper } from '../../../helper/action.helper';
import { Subject } from 'rxjs';

@Component({
  selector: 'fi-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  standalone: false,
})
export class UserProfileComponent implements OnInit, OnDestroy {
  readonly actionHelper = ActionHelper;

  userForm = this.fb.group({
    FirstName: this.fb.control('', [Validators.required, Validators.maxLength(64)]),
    LastName: this.fb.control('', [Validators.required, Validators.maxLength(64)]),
    Email: this.fb.control('', [Validators.required, Validators.maxLength(320)]),
    Phone: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator), Validators.maxLength(32)]),
    PhoneCountryCode: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    Mobile: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    MobileCountryCode: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    LanguageCode: this.fb.control('', Validators.required),
    NotificationsEnabled: this.fb.control(false),
    LeadMailEnabled: this.fb.control(false),
    AppointmentMailEnabled: this.fb.control(false),
    ReminderMailEnabled: this.fb.control(false),
    GvlMailEnabled: this.fb.control(false),
    ExternalMailCCEnabled: this.fb.control(false),
    CompletedMailEnabled: this.fb.control(false),
  });
  absenceForm = this.fb.group({
    From: this.fb.control(undefined),
    To: this.fb.control(undefined),
    Enabled: this.fb.control(false),
    AbsenceTypeId: this.fb.control(null),
    AbsenceTypeText: this.fb.control(''),
    RecurringDays: this.fb.control(undefined),
    RecurringTimeFrom: this.fb.control(undefined),
    RecurringTimeTo: this.fb.control(undefined),
    SubstituteUserId: this.fb.control(undefined),
    Timezone: this.fb.control(Intl.DateTimeFormat().resolvedOptions().timeZone)
  });

  profileData: ProfileData;
  isDealerPartner: boolean;
  absenceTypes: IAbsenceTypes[] = [];
  avatarImage: IFileDocument;
  avatarImageDataUrl: SafeUrl;
  resourceLanguages: ResourceLanguage[];
  resourceProvinces: ResourceProvinces[];
  numberPrefixes: { code: string, name: string }[] = [];
  noDateIsChecked: boolean;
  profileContentLoaded: boolean;

  dateFormIsHidden = true;
  passwordChangeURL: string;
  emptyAbsence = true;
  showMaxlengthError = false;

  increaseHeight = false;

  private showErrors = false;
  private emailValidatePatern: any = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  constructor(
    private sanitizer: DomSanitizer,
    private readonly sharedProfileService: UserProfileShareService,
    @Inject(PROFILE_DATA_SERVICE) private readonly profileDataService: ProfileDataService,
    @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
    @Inject(ACTION_DATA_SERVICE) public actionDataService: ActionDataService,
    private fb: UntypedFormBuilder,
    private store: Store
  ) {
    this.getProfileData(false);
    this.actionHelper.initializeOrResetUnsavedChanges(this);
    this.getResourceLanguages();

    /**
     * not used now
     */
      // this.getAbsenceTypes();
      // this.getResourceProvinces();
      //
      // AddressHelper.getCountries().then(data => {
      //   //@ts-ignore
      //   this.numberPrefixes = PhoneNumberHelper.getCodeList(data);
      // });

    const url = new URL(environment.passwordChangeRoot);
    url.searchParams.append('returnUrl', environment.apiRootUrl);
    this.passwordChangeURL = url.href;
  }

  ngOnInit(): void {
    this.scrollToTop();
  }

  scrollToTop(): void {
    if ( window.innerWidth <= 768 ) {
      window.scrollTo(0, 0);
    }
  }

  toggleDateForm(): void {
    this.dateFormIsHidden = !this.dateFormIsHidden;
    if ( this.dateFormIsHidden ) {
      this.noDateIsChecked = false;
    }
  }

  getAbsenceTypes(): void {
    this.profileDataService.getAbsenceTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.absenceTypes = data?.filter(type => !type.Inactive);
      }
    });
  }

  getProfileData( isUpdate?: boolean ): void {
    this.profileDataService.getUserProfile().subscribe(( { success, data, error } ) => {
      if ( success ) {
        this.profileData = data;
        this.avatarImage = this.profileData.AvatarImage;
        this.sharedProfileService.setProfile(this.profileData);
        document.body.setAttribute('data-lang', this.profileData.User.LanguageCode.toLowerCase());
        this.isDealerPartner = this.sharedProfileService.computeIsDealerPartner(this.profileData?.Tenants ?? []);
        this.profileData.Absences = data.Absences.filter(( absence, i, arr ) =>
          i === arr.findIndex(item =>
            item.From === absence.From &&
            item.To === absence.To &&
            item.RecurringDays === absence.RecurringDays &&
            item.RecurringTimeFrom === absence.RecurringTimeFrom &&
            item.RecurringTimeTo === absence.RecurringTimeTo
          ));
        this.patchProfileValue(data);
        this.emptyAbsence = !data.Absences?.length;

        if ( !this.emptyAbsence ) {
          const absences = data.Absences.filter(absence => absence.Enabled);

          if ( absences.length && this.dateFormIsHidden ) {
            this.absenceForm.patchValue({
              ...absences[absences.length - 1],
              Enabled: true
            });
            this.toggleDateForm();
          }
        }

        if ( isUpdate ) {
          this.store.dispatch(new HideLoader());
        }

        this.profileContentLoaded = true;
      }
    });
  }

  patchProfileValue( data: ProfileData ): void {
    const phone = PhoneNumberHelper.getSplitPhoneNumber(data.User.Phone);
    const mobile = PhoneNumberHelper.getSplitPhoneNumber(data.User.Mobile);
    this.userForm.patchValue({
      FirstName: data.User.FirstName,
      LastName: data.User.LastName,
      Email: data.User.Email,
      Phone: phone.phone,
      PhoneCountryCode: phone.code,
      Mobile: mobile.phone,
      MobileCountryCode: mobile.code,
      LanguageCode: data.User.LanguageCode,
      NotificationsEnabled: data.User.NotificationsEnabled,
      LeadMailEnabled: data.User.LeadMailEnabled,
      AppointmentMailEnabled: data.User.AppointmentMailEnabled,
      ReminderMailEnabled: data.User.ReminderMailEnabled,
      GvlMailEnabled: data.User.GvlMailEnabled,
      ExternalMailCCEnabled: data.User.ExternalMailCCEnabled,
      CompletedMailEnabled: data.User.CompletedMailEnabled,
    });
  }

  onImageUpload( event: Event ): void {
    const inputEl = event.target as HTMLInputElement;
    UploadHelper.uploadFile(inputEl.files[0], /image\/*/)
      .then(res => {
        this.actionHelper.notifyAboutEdit();
        this.avatarImage = res;
        this.avatarImageDataUrl = this.sanitizer.bypassSecurityTrustUrl(UploadHelper.getDataUrlFromDocument(this.avatarImage));
      })
      .catch(err => {
        console.log(err);
      });
  }

  private isValidMaxlength(): boolean {
    const formContact = Object.keys(this.userForm.controls);
    let isValid = false;

    formContact.forEach(item => {
      if ( !!this.userForm.controls[item].errors?.maxlength ) {
        isValid = true;
      }
    });

    return isValid;
  }

  onFormSubmit(): boolean {
    if ( !this.profileData || this.userForm.invalid ) {

      if ( this.isValidMaxlength() ) {
        this.showMaxlengthError = true;
      }
      this.showErrors = true;
      setTimeout(() => {
        this.showErrors = false;
      }, 5000);
      return false;
    }
    if ( !this.dateFormIsHidden ) {
      if ( !this.validateAbsenceForm() ) {
        return false;
      }
    }
    const absences = [this.absenceForm.getRawValue()];

    if ( this.dateFormIsHidden ) {
      absences[0] = {
        Enabled: false
      } as any
    } else {
      for ( const key of Object.keys(absences[0]) ) {
        if ( absences[0][key] === null ) {
          delete absences[0][key];
        }
      }
    }

    this.updateUserProfile(absences);
    return false;
  }

  updateUserProfile( absences: any ): void {
    const Phone = PhoneNumberHelper.getPhoneNumber(this.userForm.controls.PhoneCountryCode.value, this.userForm.controls.Phone.value);
    const Mobile = PhoneNumberHelper.getPhoneNumber(this.userForm.controls.MobileCountryCode.value, this.userForm.controls.Mobile.value);
    const requestBody: UserUpdateData | any = {
      Id: this.profileData.User.Id,
      ...this.userForm.getRawValue(),
      Phone,
      Mobile,
      PhoneCountryCode: undefined,
      MobileCountryCode: undefined,
      Absences: absences,
      AvatarImage: this.avatarImage as any,
    };
    this.store.dispatch(new ShowLoader());
    this.profileDataService.updateUserProfile(requestBody).subscribe(( { success, error } ) => {
      if ( success ) {
        this.getResourceLanguages();
        this.getProfileData(true);
        // this.getCountries();
        // this.getResourceProvinces();
        // this.getNotInterestedTypes();
        this.actionHelper.initializeOrResetUnsavedChanges(this);
      } else {
        console.log(error);
      }
    });
  }

  getResourceLanguages(): void {
    this.resourcesDataService.getResourceLanguages().subscribe(( { success, data } ) => {
      if ( success ) {
        this.resourceLanguages = data.filter(language => !['cs'].includes(language.IsoCode));
      }
    });
  }

  getResourceProvinces(): void {
    this.resourcesDataService.getResourceProvinces().subscribe(( { success, data } ) => {
      if ( success ) {
        this.resourceProvinces = data;
        AddressHelper.setProvinces(data);
      }
    });
  }

  getCountries(): void {
    this.resourcesDataService.getResourceCountries().subscribe(( { success, data } ) => {
      if ( success ) {
        AddressHelper.setCountries(data);
      }
    });
  }

  getNotInterestedTypes(): void {
    this.actionDataService.getNotInterestedTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        HistoryCommentComponent.notInterestedTypes = data as NotInterestedTypes[];
      }
    });
  }

  invalidFormControl( name: string ): boolean {
    if ( name === 'Email' && this.profileContentLoaded ) {
      const email = this.userForm.controls[name].value;

      return !(this.emailValidatePatern.test(email) && email.length <= 320);
    }
    return this.userForm.controls[name].invalid &&
      (this.userForm.controls[name].touched || this.userForm.controls[name].dirty || this.showErrors);
  }

  invalidMaxLength( name: string ): boolean {
    return this.showMaxlengthError && !!this.userForm.controls[name].errors?.maxlength;
  }

  setAbsenceDates( dates: Date[] ): void {
    this.absenceForm.controls.From.setValue(dates[0]?.toISOString());
    this.absenceForm.controls.To.setValue(dates[1]?.toISOString());
    this.absenceForm.controls.AbsenceTypeId.setValue(1);
    this.absenceForm.controls.AbsenceTypeText.setValue('URLAUB');

    setTimeout(() => {
      this.validateAbsenceForm();
    });
  }

  setRecurringData( data: RecurringDate ): void {
    this.absenceForm.controls.RecurringDays.setValue(data.RecurringDays);
    this.absenceForm.controls.RecurringTimeFrom.setValue(data.RecurringTimeFrom);
    this.absenceForm.controls.RecurringTimeTo.setValue(data.RecurringTimeTo);

    setTimeout(() => {
      this.validateAbsenceForm();
    });
  }

  private validateAbsenceForm(): boolean {
    if ( !this.absenceForm.controls.From.value &&
      !this.absenceForm.controls.To.value &&
      !this.absenceForm.controls.RecurringDays.value
    ) {
      this.noDateIsChecked = true;
    } else {
      this.noDateIsChecked = false;
    }

    this.emptyAbsence = this.noDateIsChecked;
    this.absenceForm.controls.Enabled.setValue( !this.emptyAbsence);
    return !this.noDateIsChecked;
  }

  ngOnDestroy(): void {
    this.actionHelper.clearUnsavedChanges(this);
  }
}
